import { DateSelectArg, EventChangeArg } from '@fullcalendar/core';
import { DropArg } from '@fullcalendar/interaction';
import {
  Goal,
  ID,
  LifeArea,
  NewTodo,
  Task,
  TaskActionType,
  TaskType,
  Timeslot,
  Timestamp,
} from '@griply/types';
import {
  createPlannedOccurrenceForDate,
  getCalendarTaskDuration,
  getCalendarTaskStartTime,
  getWeekStartsOn,
} from '@griply/utils';
import { differenceInMilliseconds, endOfDay, startOfDay } from 'date-fns';
import { useUpdateGoal } from 'features/goals';
import { useCreateTask, useUpdateTask } from 'features/tasks';
import { useCallback } from 'react';
import { halfHour } from 'shared/constants';
import { useOpenCreateTask } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { DataType } from 'shared/types/data-type';

const createTimeslotFromCalendarEvent = (
  arg: EventChangeArg | DropArg,
  externalDurationMs?: number | null,
): Timeslot | null => {
  const isDropArg = !('event' in arg);
  const fcEvent = isDropArg ? null : arg.event;

  const allDay = isDropArg ? arg.allDay : fcEvent!.allDay;

  if (allDay) {
    return null;
  }

  const start = isDropArg ? arg.date : fcEvent!.start;
  const end = isDropArg ? null : fcEvent!.end;

  if (!start) {
    return null; // Should not happen for non-allDay events
  }

  const durationMs = end
    ? getCalendarTaskDuration(start, end)
    : fcEvent?.extendedProps?.duration || externalDurationMs || halfHour;

  return {
    startTime: getCalendarTaskStartTime(start),
    duration: durationMs,
  };
};

export const useCalendarEventControls = (
  lifeAreas: LifeArea[],
  goals: Goal[],
  tasks: Task[],
) => {
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const today = useToday();

  const { submit: updateTask } = useUpdateTask();
  const { submit: createTask } = useCreateTask();
  const { submit: updateGoal } = useUpdateGoal();
  const openCreateTask = useOpenCreateTask();

  const createTaskFromGoal = useCallback(
    (
      goalId: ID,
      goalName: string,
      eventDate: Timestamp,
      eventArg: EventChangeArg | DropArg,
      externalDurationMs?: number | null,
    ) => {
      const task: NewTodo = {
        type: TaskType.Task,
        schedules: null,
        endStrategy: {
          deadline: endOfDay(eventDate),
          completionCount: null,
        },
        createdAt: new Date(),
        goalId,
        name: goalName,
        lifeAreaId: null,
        reminderTime: null,
        startDate: null,
        parentIds: null,
        priority: null,
        hideOnTodo: null,
        timeslot: createTimeslotFromCalendarEvent(eventArg, externalDurationMs),
        action: {
          type: TaskActionType.GoalTimeslot,
          meta: { id: goalId },
        },
      };
      createTask(task);
    },
    [createTask],
  );

  const createTaskFromLifeArea = useCallback(
    (
      lifeAreaId: ID,
      lifeAreaName: string,
      eventDate: Timestamp,
      eventArg: EventChangeArg | DropArg,
      externalDurationMs?: number | null,
    ) => {
      const task: NewTodo = {
        type: TaskType.Task,
        schedules: null,
        endStrategy: {
          deadline: endOfDay(eventDate),
          completionCount: null,
        },
        createdAt: new Date(),
        lifeAreaId,
        name: lifeAreaName,
        goalId: null,
        reminderTime: null,
        startDate: null,
        parentIds: null,
        priority: null,
        hideOnTodo: null,
        timeslot: createTimeslotFromCalendarEvent(eventArg, externalDurationMs),
        action: {
          type: TaskActionType.LifeAreaTimeslot,
          meta: { id: lifeAreaId },
        },
      };
      createTask(task);
    },
    [createTask],
  );

  const handleGoalUpdate = useCallback(
    (goal: Goal, date: Timestamp) => {
      updateGoal({
        id: goal.id,
        deadline: endOfDay(date),
      });
    },
    [updateGoal],
  );

  const handleTaskUpdate = useCallback(
    (
      task: Task,
      date: Timestamp,
      timeslot: Timeslot | null,
      isDeadline: boolean = false,
    ) => {
      if (isDeadline) {
        updateTask({
          id: task.id,
          deadlineDeadline: endOfDay(date),
        });
        return;
      }

      // habit or repeating task
      if (task.type !== TaskType.Task) {
        // create new schedules with the updated timeslot
        const schedules = createPlannedOccurrenceForDate(task.schedules, date, {
          timeslot,
          today,
          weekStartsOn,
        });

        // update the schedule with a `timeslot` based on the event
        updateTask({ id: task.id, schedules });
        return;
      }

      // regular task
      updateTask({
        id: task.id,
        endStrategy: {
          completionCount: null,
          deadline: endOfDay(date),
        },
        timeslot,
      });
    },
    [today, updateTask, weekStartsOn],
  );

  const onEventChange = useCallback(
    (changeEvent: EventChangeArg) => {
      const { extendedProps, start, allDay } = changeEvent.event;
      const { start: oldStart } = changeEvent.oldEvent;

      if (!start) {
        return;
      }

      // Handle life-area events (skip task events that are merely linked to a life area)
      if (extendedProps.lifeAreaId && !extendedProps.taskId) {
        const lifeArea = lifeAreas.find(
          ({ id }) => id === extendedProps.lifeAreaId,
        );
        if (!lifeArea) {
          return;
        }

        // If the life-area is dragged outside allDay, create a new task
        createTaskFromLifeArea(lifeArea.id, lifeArea.name, start, changeEvent);
        return;
      }

      // Handle goal events (skip task events that are merely linked to a goal)
      if (extendedProps.goalId && !extendedProps.taskId) {
        const goal = goals.find(({ id }) => id === extendedProps.goalId);
        if (!goal) {
          return;
        }

        // If the goal is moved within allDay, update its deadline
        if (allDay) {
          handleGoalUpdate(goal, start);
          return;
        }

        // If the goal is dragged outside allDay, create a new task
        createTaskFromGoal(goal.id, goal.name, start, changeEvent);
        return;
      }

      // Handle task events
      const task = tasks.find(({ id }) => id === extendedProps.taskId);
      if (!task) {
        return;
      }

      // repeating and habit tasks get a planned occurrence when the user moves a slot
      if (task.type !== TaskType.Task) {
        const timeslot = createTimeslotFromCalendarEvent(changeEvent);

        const schedules = createPlannedOccurrenceForDate(
          task.schedules,
          start,
          {
            timeslot,
            weekStartsOn,
            today,
            originalDate: oldStart ?? undefined,
            id: extendedProps.occurrenceId,
          },
        );

        // update the schedule with a `timeslot` based on the event
        updateTask({ id: task.id, schedules });
        return;
      }

      handleTaskUpdate(
        task,
        start,
        createTimeslotFromCalendarEvent(changeEvent),
        extendedProps.isDeadline,
      );
    },
    [
      tasks,
      handleTaskUpdate,
      lifeAreas,
      createTaskFromLifeArea,
      goals,
      createTaskFromGoal,
      handleGoalUpdate,
      weekStartsOn,
      today,
      updateTask,
    ],
  );

  const onDrop = useCallback(
    (event: DropArg) => {
      const { draggedEl, date, allDay } = event;
      const dataEl = draggedEl.querySelector('[data-component-type]');
      const el = dataEl ?? draggedEl;

      const id = el.getAttribute('data-id');

      if (!date || !id) {
        return;
      }

      const type = el.getAttribute('data-component-type') as
        | DataType
        | undefined;
      const title = el.getAttribute('data-title'); // Used as fallback or direct name
      const rawDuration = el.getAttribute('data-duration');
      const customDurationMs = rawDuration ? parseInt(rawDuration) : null;
      // Ensure customDurationMs is valid (positive number), otherwise treat as null.
      const validCustomDurationMs = customDurationMs || null;

      if (type === DataType.LifeArea) {
        const lifeArea = lifeAreas.find((lifeArea) => lifeArea.id === id);
        if (!lifeArea || !title) {
          return;
        }

        // If the lifeArea is dropped outside allDay, create a new task
        createTaskFromLifeArea(
          lifeArea.id,
          title,
          date,
          event,
          validCustomDurationMs,
        );
        return;
      }

      if (type === DataType.Goal) {
        if (!title) {
          return;
        }

        // If the goal is dropped in allDay, update its deadline
        if (allDay) {
          const goal = goals.find((goal) => goal.id === id);
          if (goal) {
            handleGoalUpdate(goal, date);
          }
          return;
        }
        // Create task using id and title from attributes, as per original logic for non-allDay drops.
        createTaskFromGoal(id, title, date, event, validCustomDurationMs);
        return;
      }

      const task = tasks.find((task) => task.id === id);
      if (!task) {
        return;
      }

      handleTaskUpdate(
        task,
        date,
        createTimeslotFromCalendarEvent(event, validCustomDurationMs),
        false,
      );
    },
    [
      tasks,
      handleTaskUpdate,
      lifeAreas,
      createTaskFromLifeArea,
      goals,
      createTaskFromGoal,
      handleGoalUpdate,
    ],
  );

  const onSelect = useCallback(
    ({ start, end, view }: DateSelectArg) => {
      const dayStart = startOfDay(start);
      const startTime = differenceInMilliseconds(start, dayStart);
      const duration = differenceInMilliseconds(end, start);

      const partialTodo: Partial<NewTodo> = {
        timeslot: { startTime, duration },
        endStrategy: {
          completionCount: null,
          deadline: endOfDay(start),
        },
      };

      openCreateTask(partialTodo);
      // added to disable the selected view when escape is pressed in the createTask modal
      view.calendar.unselect();
    },
    [openCreateTask],
  );

  const onDateTime = useCallback(
    (info: { date: Date; allDay: boolean }) => {
      const clickedDate = info.date;
      const dayStart = startOfDay(clickedDate);
      const timeslot = info.allDay
        ? null
        : {
            startTime: differenceInMilliseconds(clickedDate, dayStart),
            // Default duration of 1 hour
            duration: 60 * 60 * 1000,
          };

      const partialTodo: Partial<NewTodo> = {
        timeslot,
        endStrategy: {
          completionCount: null,
          deadline: endOfDay(clickedDate),
        },
      };

      openCreateTask(partialTodo);
    },
    [openCreateTask],
  );

  return {
    handleTaskUpdate,
    handleGoalUpdate,
    onEventChange,
    onDrop,
    onSelect,
    onDateTime,
  };
};
